import {
  Dialog,
  FormCheckbox,
  FormInput,
  FormSelect,
  FormTextarea,
  Loader,
} from "@/components";
import { FormProvider } from "react-hook-form";
import useAddEditGlMaster from "./useAddEditGlMaster";
import { Button, DialogClose, DialogFooter } from "@/components/ui";
import {
  ACCOUNT_CATEGORY,
  ACCOUNT_NO,
  ALTERNATE_ACC,
  ALTERNATE_ACC_CUSTOM,
  CLEARING_ACCOUNT,
  COMMENT,
  COST_CENTER,
  GROUP_GL,
  LONG_DESC,
  NO_DIRECT_POSTING,
  ORDER_NUMBER,
  PROFIT_CENTER,
  RECON_ACCOUNT,
  SHORT_DESC,
  SUBLEDGER,
  VIRTUAL_ACC,
} from "./consts";
import {
  accountCategoryOptions,
  subledgerOptions,
} from "@/lib/utils";
import { AddEditGlMasterProps } from "./types";

const AddEditGlMaster = ({
  open,
  setOpen,
  isView,
  onCloseDialog,
  rowSelected,
}: AddEditGlMasterProps) => {
  const {
    onSubmit,
    form,
    requiredFields,
    isUpdatePending,
    isAddPending,
    isLoadingGlMaster,
    openConfirmation,
    setOpenConfirmation,
    apiCall,
    keys,
    alternateOptions,
    isAlternateOther,
    isLoadingVirtualAccList,
    virtualAccList
  } = useAddEditGlMaster({
    rowSelected,
    onCloseDialog,
  });
  const { errors } = form.formState;

  return (
    <>
      <Dialog
        open={open}
        setOpen={setOpen}
        title={
          isView
            ? "View GL Master"
            : rowSelected
            ? "Edit GL Master"
            : "Add GL Master"
        }
        contentClassName="lg:!w-[1000px] lg:!max-w-4xl"
        hideFooter
      >
        {isLoadingGlMaster || isLoadingVirtualAccList ? (
          <Loader />
        ) : (
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-[30px]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-[30px]">
                  <FormInput
                    name={ACCOUNT_NO}
                    control={form.control}
                    label="Account No"
                    placeholder="Please enter"
                    error={errors[ACCOUNT_NO]?.message}
                    isRequired={requiredFields.includes(ACCOUNT_NO)}
                    disabled={isView}
                    variant="integer"
                    maxLength={5}
                  />
                  <FormSelect
                    name={VIRTUAL_ACC}
                    control={form.control}
                    label="Virtual Account"
                    placeholder="Please enter"
                    error={errors[VIRTUAL_ACC]?.message}
                    isRequired={requiredFields.includes(VIRTUAL_ACC)}
                    disabled={isView}
                    variant="integer"
                    maxLength={20}
                    options={virtualAccList}
                  />
                  <FormSelect
                    key={keys.alternativeAcc}
                    name={ALTERNATE_ACC}
                    control={form.control}
                    label="Alternate Account"
                    placeholder="Please enter"
                    error={errors[ALTERNATE_ACC]?.message}
                    isRequired={requiredFields.includes(ALTERNATE_ACC)}
                    disabled={isView}
                    variant="integer"
                    maxLength={20}
                    options={alternateOptions}
                  />
                  {isAlternateOther && (
                    <FormInput
                      name={ALTERNATE_ACC_CUSTOM}
                      control={form.control}
                      label="Other Alternate Account"
                      placeholder="Please enter"
                      error={errors[ALTERNATE_ACC_CUSTOM]?.message}
                      isRequired
                      disabled={isView}
                      variant="integer"
                      maxLength={20}
                    />
                  )}
                
                  <FormSelect
                    name={ACCOUNT_CATEGORY}
                    control={form.control}
                    label="Account Category"
                    placeholder="Please select"
                    error={errors[ACCOUNT_CATEGORY]?.message}
                    isRequired={requiredFields.includes(ACCOUNT_CATEGORY)}
                    options={accountCategoryOptions}
                    disabled={isView}
                  />
                  <FormSelect
                    name={SUBLEDGER}
                    control={form.control}
                    label="Subledger"
                    placeholder="Please select"
                    error={errors[SUBLEDGER]?.message}
                    isRequired={requiredFields.includes(SUBLEDGER)}
                    options={subledgerOptions}
                    disabled={isView}
                  />
                  {/* <FormSelect
                    name={SUBLEDGER_TYPE}
                    control={form.control}
                    label="Subledger Type"
                    placeholder="Please select"
                    error={errors[SUBLEDGER_TYPE]?.message}
                    isRequired={requiredFields.includes(SUBLEDGER_TYPE)}
                    options={subledgerTypeOptions}
                    disabled={isView}
                  /> */}

                  <div className="lg:col-span-2">
                    <FormInput
                      name={SHORT_DESC}
                      control={form.control}
                      label="Short Description"
                      placeholder="Please enter"
                      error={errors[SHORT_DESC]?.message}
                      isRequired={requiredFields.includes(SHORT_DESC)}
                      disabled={isView}
                      maxLength={50}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px]">
                  <FormCheckbox
                    name={GROUP_GL}
                    control={form.control}
                    label="Group Allocation GL"
                    error={errors[GROUP_GL]?.message}
                    isRequired={requiredFields.includes(GROUP_GL)}
                    disabled={isView}
                  />
                  <FormCheckbox
                    name={RECON_ACCOUNT}
                    control={form.control}
                    label="Recon Account"
                    error={errors[RECON_ACCOUNT]?.message}
                    isRequired={requiredFields.includes(RECON_ACCOUNT)}
                    disabled={isView}
                  />
                  <FormCheckbox
                    name={PROFIT_CENTER}
                    control={form.control}
                    label="Profit Center"
                    error={errors[RECON_ACCOUNT]?.message}
                    isRequired={requiredFields.includes(RECON_ACCOUNT)}
                    disabled={isView}
                  />
                  <FormCheckbox
                    name={ORDER_NUMBER}
                    control={form.control}
                    label="Order Number"
                    error={errors[ORDER_NUMBER]?.message}
                    isRequired={requiredFields.includes(ORDER_NUMBER)}
                    disabled={isView}
                  />
                  <FormCheckbox
                    name={COST_CENTER}
                    control={form.control}
                    label="Cost Center"
                    error={errors[COST_CENTER]?.message}
                    isRequired={requiredFields.includes(COST_CENTER)}
                    disabled={isView}
                  />
                  <FormCheckbox
                    name={NO_DIRECT_POSTING}
                    control={form.control}
                    label="No Direct Posting"
                    error={errors[NO_DIRECT_POSTING]?.message}
                    isRequired={requiredFields.includes(NO_DIRECT_POSTING)}
                    disabled={isView}
                  />
                  <FormCheckbox
                    name={CLEARING_ACCOUNT}
                    control={form.control}
                    label="Clearing Account"
                    error={errors[CLEARING_ACCOUNT]?.message}
                    isRequired={requiredFields.includes(CLEARING_ACCOUNT)}
                    disabled={isView}
                  />
                </div>
                <FormInput
                  name={LONG_DESC}
                  control={form.control}
                  label="Long Description"
                  placeholder="Please enter"
                  error={errors[LONG_DESC]?.message}
                  isRequired={requiredFields.includes(LONG_DESC)}
                  disabled={isView}
                  maxLength={100}
                />
                <FormTextarea
                  name={COMMENT}
                  control={form.control}
                  label="Comment"
                  placeholder="Please enter"
                  error={errors[COMMENT]?.message}
                  isRequired={requiredFields.includes(COMMENT)}
                  disabled={isView}
                  maxLength={500}
                />
              </div>
              <DialogFooter className="mt-5">
                <DialogClose>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isUpdatePending || isAddPending}
                    onClick={onCloseDialog}
                    className="w-full mg:w-auto"
                  >
                    Cancel
                  </Button>
                </DialogClose>
                {!isView && (
                  <Button
                    type="submit"
                    disabled={isUpdatePending || isAddPending}
                  >
                    {isUpdatePending ||
                      (isAddPending && <Loader variant="secondary" />)}
                    Save
                  </Button>
                )}
              </DialogFooter>
            </form>
          </FormProvider>
        )}
      </Dialog>
      <Dialog
        open={openConfirmation}
        setOpen={setOpenConfirmation}
        title={"Alternative Account Confirmation"}
        contentClassName="lg:!w-[1000px] lg:!max-w-4xl"
        saveText="Yes"
        cancelText="No"
        onSave={apiCall}
      >
        <div>Are you sure you want to modify the Alternative Account?</div>
      </Dialog>
    </>
  );
};

export default AddEditGlMaster;
